<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import AcceptTerms from '@/Components/AcceptTerms.vue';
import { Head, router } from '@inertiajs/vue3';
import PhoneInput from '@/Components/PhoneInput.vue';
import Swal from 'sweetalert2';
</script>

<script>
export default {
    props: {
        errors: {
            type: Object
        }
    },
    data() {
        return {
            userData: {
                name: '',
                email: '',
                phone: '',
                username: '',
                password: '',
                passwordRepeat: '',
                termsAccepted: false,
            }
        }
    },
    methods: {

        emailChanged() {
            if (!this.userData.username) {
                this.userData.username = this.userData.email;
            }
        },

        registerUser() {
            router.post(route('register'), {
                name: this.userData.name,
                email: this.userData.email,
                phone: this.userData.phone,
                username: this.userData.username,
                password: this.userData.password,
                passwordRepeat: this.userData.passwordRepeat,
                termsAccepted: this.userData.termsAccepted,
                redirect: true
            }, {
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: () => {
                    if (dataLayer) {
                        dataLayer.push({
                            event: 'registration',
                            email: this.userData.email,
                            phone: this.userData.phone,
                        });
                    }
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
            });
        },

    },
    mounted() {

        // Page view DataLayer event
        if (dataLayer) {
            dataLayer.push({
                event: 'virtualPageView',
                page_type: 'other',
                site_language: this.$page.props.locale?.toUpperCase() || '?',
                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                user_id: this.$page.props.user ? this.$page.props.user.id : null,
            });
        }

    }
}
</script>

<template>

    <Head :title="$t('Regisztráció')" />

    <AppLayout>

        <header class="header">
            <div class="header-body header-body-subpage">
                <div class="container">
                    <div class="row align-items-center justify-content-center">
                        <div class="col-12 col-sm-auto col-lg text-center text-sm-right text-lg-left">
                            <h1 class="header-h1">{{ $t('Regisztráció') }}</h1>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <div class="header-placeholder"></div>

        <div class="subpage-content">
            <div class="container subpage-container">
                <div class="row">
                    <main class="main col-12">
                        <section>
                            <div class="row section-title d-none">
                                <div class="col-12">
                                    <h2>{{ $t('Regisztráció') }}</h2>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <form @submit.prevent="registerUser">
                                        <p class="fs-6 text-center" v-html="$t('Foglalásainak intézéséhez töltse ki a regisztrációs űrlapot, így gyorsabban és könnyebben foglalhat.')">
                                        </p>
                                        <div v-if="errors.form" class="alert alert-danger fw-bold mb-3" role="alert">{{ errors.form }}</div>
                                        <div class="row">
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors.name }" id="name" :placeholder="$t('Név')" v-model="userData.name" maxlength="150">
                                                    <label for="name">{{ $t('Név') }}<span class="text-danger">*</span></label>
                                                    <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="email" class="form-control" :class="{ 'is-invalid': errors.email }" id="email" :placeholder="$t('E-mail cím')" v-model="userData.email" maxlength="150" @change="emailChanged">
                                                    <label for="email">{{ $t('E-mail cím') }}<span class="text-danger">*</span></label>
                                                    <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <PhoneInput id="phone" class="form-control d-flex" :class="{ 'is-invalid': errors.phone }" :placeholder="$t('Mobiltelefonszám')" v-model="userData.phone" />
                                                    <label for="phone">{{ $t('Mobiltelefonszám') }}<span class="text-danger">*</span></label>
                                                    <div class="invalid-feedback" v-if="errors.phone">{{ errors.phone }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors.username }" id="username" name="username" :placeholder="$t('Felhasználói név')" v-model="userData.username">
                                                    <label for="username">{{ $t('Felhasználói név') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors.username" class="invalid-feedback">{{ errors.username }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="password" class="form-control" :class="{ 'is-invalid': errors.password }" id="password" name="password" :placeholder="$t('Jelszó')" v-model="userData.password">
                                                    <label for="password">{{ $t('Jelszó') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="password" class="form-control" :class="{ 'is-invalid': errors.passwordRepeat }" id="passwordRepeat" name="passwordRepeat" :placeholder="$t('Jelszó ismét')" v-model="userData.passwordRepeat">
                                                    <label for="passwordRepeat">{{ $t('Jelszó ismét') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors.passwordRepeat" class="invalid-feedback">{{ errors.passwordRepeat }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-12">
                                                <div class="form-check mb-3">
                                                    <AcceptTerms class="form-check-input" :class="{ 'is-invalid': errors.termsAccepted }" id="acceptTerms" v-model="userData.termsAccepted" />
                                                    <div v-if="errors.termsAccepted" class="invalid-feedback">{{ errors.termsAccepted }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="mb-3">
                                            <button type="submit" class="btn btn-primary px-5 text-uppercase">{{ $t('Regisztráció') }}</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        </div>

    </AppLayout>
</template>
